// Dynamically load and inject JSON-LD from backend
export function loadAndInjectJsonLd(params) {
    // logger.debug('[JSON-LD] loadAndInjectJsonLd called with params:', params)
    
    let url = '/jsonld'
    if (params && Object.keys(params).length > 0) {
        const query = new URLSearchParams(params).toString()
        url += '?' + query
        // logger.debug('[JSON-LD] Query string built:', query)
    // } else {
        // logger.debug('[JSON-LD] No params provided, using default URL')
    }
    
    // logger.debug('[JSON-LD] Fetching from URL:', url)
    
    return fetch(url)
        .then(res => {
            // logger.debug('[JSON-LD] Response received - Status:', res.status, 'OK:', res.ok)
            if (!res.ok) {
                const error = `Failed to fetch JSON-LD (HTTP ${res.status})`
                // logger.error('[JSON-LD] ' + error)
                throw new Error(error)
            }
            return res.text()
        })
        .then(jsonld => {
            // logger.debug('[JSON-LD] Received text content, length:', jsonld.length)
            // logger.debug('[JSON-LD] Content preview:', jsonld.substring(0, 200))
            
            // Remove any previous injected JSON-LD
            const prev = document.getElementById('dynamic-jsonld')
            if (prev) {
                // logger.debug('[JSON-LD] Found previous JSON-LD script, removing')
                prev.remove()
            } else {
                // logger.debug('[JSON-LD] No previous JSON-LD script found')
            }
            
            // Inject new JSON-LD
            const script = document.createElement('script')
            script.type = 'application/ld+json'
            script.id = 'dynamic-jsonld'
            script.textContent = jsonld
            document.head.appendChild(script)
            // logger.debug('[JSON-LD] Successfully injected JSON-LD script into <head>')
            // logger.debug('[JSON-LD] Script element:', script)
        })
        .catch(e => {
            // logger.error('[JSON-LD] injection failed:', e.message, e)
        })
}

// Inject SEO meta tags from page data
export function injectSeoMetaTags(seoData) {
    // logger.debug('[SEO] injectSeoMetaTags called with:', seoData)
    
    if (!seoData) {
        // logger.debug('[SEO] No SEO data provided, skipping meta tag injection')
        console.warn('[SEO] No SEO data provided')
        return
    }

    const { description, keywords, title } = seoData

    // Inject meta description
    if (description) {
        let metaDescription = document.querySelector('meta[name="description"]')
        if (!metaDescription) {
            metaDescription = document.createElement('meta')
            metaDescription.name = 'description'
            metaDescription.setAttribute('data-seo-source', 'page-seo_description')
            document.head.appendChild(metaDescription)
            // logger.debug('[SEO] Created new meta description tag')
        }
        metaDescription.content = description
        // logger.debug('[SEO] Set meta description:', description)
    }

    // Inject meta keywords
    if (keywords) {
        let metaKeywords = document.querySelector('meta[name="keywords"]')
        if (!metaKeywords) {
            metaKeywords = document.createElement('meta')
            metaKeywords.name = 'keywords'
            metaKeywords.setAttribute('data-seo-source', 'page-seo_keywords')
            document.head.appendChild(metaKeywords)
            // logger.debug('[SEO] Created new meta keywords tag')
        }
        metaKeywords.content = keywords
        // logger.debug('[SEO] Set meta keywords:', keywords)
    }

    // Inject OG tags for social sharing (using SEO image if available)
    if (seoData.imageUrl) {
        let ogImage = document.querySelector('meta[property="og:image"]')
        if (!ogImage) {
            ogImage = document.createElement('meta')
            ogImage.setAttribute('property', 'og:image')
            ogImage.setAttribute('data-seo-source', 'page-seo_image_id')
            document.head.appendChild(ogImage)
        }
        ogImage.content = seoData.imageUrl
        // logger.debug('[SEO] Set og:image:', seoData.imageUrl)
    }

    if (title) {
        let ogTitle = document.querySelector('meta[property="og:title"]')
        if (!ogTitle) {
            ogTitle = document.createElement('meta')
            ogTitle.setAttribute('property', 'og:title')
            ogTitle.setAttribute('data-seo-source', 'page-title')
            document.head.appendChild(ogTitle)
        }
        ogTitle.content = title
        // logger.debug('[SEO] Set og:title:', title)
    }

    if (description) {
        let ogDescription = document.querySelector('meta[property="og:description"]')
        if (!ogDescription) {
            ogDescription = document.createElement('meta')
            ogDescription.setAttribute('property', 'og:description')
            ogDescription.setAttribute('data-seo-source', 'page-seo_description')
            document.head.appendChild(ogDescription)
        }
        ogDescription.content = description
        // logger.debug('[SEO] Set og:description:', description)
    }

    // logger.debug('[SEO] SEO meta tags injection complete')
}

import { logger } from 'helpers/logger'
import { getMapsApiKey, getReCaptchaSiteKey, getSentryDSN } from 'helpers/config'
import * as fnc from 'helpers/fnc'


let mapsInitialized = false
window.initMaps = (x) => {
    mapsInitialized = true
}
export function loadMapScripts() {
    return fnc.loadScript('googleMaps', `https://maps.googleapis.com/maps/api/js?key=${getMapsApiKey()}&libraries=places,geometry&callback=initMaps`)
        .then(() => {
            return new Promise((resolve, reject) => {
                // Need this because it seems to take a moment to register
                const tryMaps = (tries = 5) => {
                    try {
                        if (window.google && window.google.maps && mapsInitialized) {
                            resolve()
                        } else {
                            throw new Error('Missing window.google.maps')
                        }
                    } catch (e) {
                        if (tries > 0) {
                            setTimeout(() => {
                                tryMaps(tries - 1)
                            }, 1000)
                        } else {
                            reject('Failed to load maps', e)
                        }
                    }
                }
                tryMaps()
            })
        })
}
export function loadBabylonJSScripts() {
    return Promise.all([
        fnc.loadScript('babylonjs-A', 'https://cdn.babylonjs.com/babylon.js'),
        fnc.loadScript('babylonjs-B', 'https://cdn.babylonjs.com/loaders/babylonjs.loaders.min.js'),
        fnc.loadScript('babylonjs-C', 'https://code.jquery.com/pep/0.4.3/pep.js'),
    ])
}

let gapiLoaded = false
export function loadOAuthScripts() {
    return new Promise((resolve, reject) => {
        fnc.loadScript('oauth', 'https://apis.google.com/js/platform.js')
            .then(() => {
                // Need this because it seems to take a moment to register
                const tryGAPI = (tries = 5) => {
                    try {
                        if (gapi) {
                            if (!gapiLoaded) {
                                gapiLoaded = true
                                gapi.load('auth2', resolve)
                            }
                        } else {
                            throw new Error('Missing gapi')
                        }
                    } catch (e) {
                        if (tries > 0) {
                            setTimeout(() => {
                                tryGAPI(tries - 1)
                            }, 100)
                        }
                    }
                }
                tryGAPI()
            })
            .catch(reject)
    })
}

export function loadGoogleIdentityScripts() {
    return new Promise((resolve, reject) => {
        fnc.loadScript('googleIdentity', 'https://accounts.google.com/gsi/client')
            .then(() => {
                const tryGoogle = (tries = 5) => {
                    try {
                        if (google) {
                            resolve()
                        } else {
                            throw new Error('Missing google identity')
                        }
                    } catch (e) {
                        if (tries > 0) {
                            setTimeout(() => {
                                tryGoogle(tries - 1)
                            }, 100)
                        }
                    }
                }
                resolve()
            })
            .catch(reject)
    })
}


function hotjarKey(key, hotjarId) { return key = `hotjar_${key}:${hotjarId}` }
export function loadHotjar(k, hotjarId) {
    const key = hotjarKey(k, hotjarId)
    logger.info(`Try Init Hotjar ${key} - prev: ${window.hotjarKey}`)
    const scriptKey = `${key}_script`
    // Prevent double load
    if (window.hotjarKey == key) {
        logger.info(`Hotjar ${key} already set up`)
        return
    }

    // If exists, clear it
    if (window.hotjarId != null) {
        const prevKey = `${window.hotjarId}_script`
        const existing = document.getElementById(prevKey)
        if (existing) {
            logger.info(`Remove previous hotjar ${prevKey}`)
            existing.remove()
        } else {
            logger.error(`Failed to remove previous hotjar ${prevKey}`)
        }

        // Suspected important variables (?)
        try {
            h.hj = null
            h._hjSettings = null
            h.hjSiteSettings = null
            h.hjBootstrap = null
            h.hjBootstrapCalled = null
            h.hjLazyModules = null
        } catch (e) {
            logger.error("Failed to clear hotjar variables", e)
        }

        window.hotjarKey = null
        window.hotjarId = null
    }

    logger.info(`Initialize Hot Jar ${key}`)
    window.hotjarKey = key
    window.hotjarId = hotjarId
    window.hj = window.hj || function () { (window.hj.q = window.hj.q || []).push(arguments) }
    window._hjSettings = { hjid: hotjarId, hjsv: 6 }
    const src = `https://static.hotjar.com/c/hotjar-${window._hjSettings.hjid}.js?sv=${window._hjSettings.hjsv}`
    return fnc.loadScript(scriptKey, src)
        .catch(logger.error)
}

export function getHotjarUserId() {
    try {
        return window.hj.globals.get('userId')
    } catch (e) {
    }
    return null
}

// function gtag() {
// window.dataLayer.push(arguments)
// }

function googleTagId(key) { return `google_tag_${key}` }

export function loadGoogleTag(k, tagIds) {
    // Initialize
    if (!window.googleTagId) {
        window.googleTagId = []
    }

    const key = googleTagId(k)
    // logger.info(`Try Init Google Tags ${key} - prev: ${window.googleTagKey}`)
    const scriptKey = `${key}_script`
    // Prevent double load
    if (window.googleTagKey == key) {
        logger.info(`Google Tag ${key} already set up`)
        return
    }

    // Remove previous
    if (window.googleTagKey != null && window.googleTagKey != key) {
        const prevKey = `${window.googleTagId}_script`
        const existing = document.getElementById(prevKey)
        if (existing) {
            logger.info(`Remove previous google tags ${prevKey}`)
            existing.remove()
        } else {
            logger.error(`Failed to remove previous google tags ${prevKey}`)
        }
        // GTM holds a reference to this array. Replacing it disconnects GTM
        // from later application events, so retain the shared data layer when
        // switching a direct Google tag identity.
        window.dataLayer = window.dataLayer || []
        window.googleTagKey = []
        window.googleTagId = []
    }

    window.googleTagId = tagIds
    logger.info(`Initialize Google Tags ${key} for Ids ${tagIds.join(', ')}`)
    window.googleTagKey = key
    return fnc.loadScript(scriptKey, `https://www.googletagmanager.com/gtag/js?id=${tagIds[0]}`)
        .then((x) => {
            window.dataLayer = window.dataLayer || []

            function gtag() { dataLayer.push(arguments); }

            gtag('js', new Date())
            tagIds.forEach((x) => {
                gtag('config', x)
            })
        })
        .catch((e) => {
            logger.error(`GTag initialization failed: ${e}`)
        })
}

function googleTagManagerId(key) { return `google_tag_manager_${key}` }

export function loadGTM(k, gtmId) {
    const key = googleTagManagerId(k)
    const scriptId = `${key}_script`
    const noscriptId = `${key}_noscript`

    if (document.getElementById(scriptId)) {
        logger.info(`GTM ${gtmId} already loaded`)
        return
    }

    // Add GTM script
    const script = document.createElement('script')
    script.id = scriptId
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');`

    document.head.appendChild(script)
    // Optionally add noscript fallback right after <body>
    document.body.insertAdjacentHTML(
        'afterbegin',
        `<noscript id="${noscriptId}"><iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`
    )
}

export function googleTagReportRegistration(key, tagId, conversionId) {
    key = googleTagId(key)
    if (window.googleTagKey != key || !window.googleTagId || !window.googleTagId.includes(tagId)) {
        return
    }
    logger.info(`Record Google Registration ${key}/${conversionId}`)
    var callback = function () {
        if (typeof (url) != 'undefined') {
            window.location = url
        }
    }
    try {
        function gtag() { dataLayer.push(arguments) }

        gtag('event', 'conversion', {
            'send_to': `${tagId}/${conversionId}`,
            'event_callback': callback,
        })
    } catch (e) {
        logger.error(e)
    }
    return false
}


function facebookPixelKey(key, pixelId) { return `facebook_pixel_${key}:${pixelId}` }

export function loadFacebookPixel(k, pixelId) {
    const key = facebookPixelKey(k, pixelId)
    // logger.info(`Try Init Facebook Pixel ${key} - prev : ${window.facebookPixelKey}`)
    const scriptKey = `${key}_script`
    if (window.facebookPixelKey == key) {
        // logger.info(`Facebook Pixel ${key} already set up`)
        return
    }

    // Strip out previous version
    if (window.facebookPixelKey != null) {
        const prevKey = `${window.facebookPixelKey}_script`
        const existing = document.getElementById(prevKey)
        if (existing) {
            logger.info(`Remove previous facebook pixel ${prevKey}`)
            existing.remove()
        } else {
            logger.error(`Failed to remove previous facebook pixel ${prevKey}`)
        }

        // Remove all scripts containing pixelId
        const scripts = [...document.getElementsByTagName('script')]
        scripts.forEach((x) => {
            if (x.src.indexOf(`signals/config/${window.facebookPixelId}`) > 0) {
                x.remove()
            }
        })

        window.fbq = null
        window._fbq = null
        window.facebookPixelKey = null
        window.facebookPixelId = null
    }

    const src = 'https://connect.facebook.net/en_US/fbevents.js'

    let n = window.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    }
    if (!window._fbq) {
        window._fbq = n
    }
    n.push = n
    n.loaded = !0
    n.version = '2.0'
    n.queue = []

    logger.info(`Intialize Facebook Pixel ${key}`)
    window.facebookPixelKey = key
    window.facebookPixelId = pixelId

    return fnc.loadScript(scriptKey, src)
        .then((x) => {
            fbq('init', pixelId)
            fbq('track', 'PageView')

            /*const pixelSrc = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`
            const img = document.createElement("img")
            img.id = `${key}_img`
            img.src = pixelSrc
            img.height = 1
            img.width = 1
            document.body.appendChild(img)*/
        })
        .catch(logger.error)
}

export function facebookPixelRecordEvent(eventName, eventData = {}) {
    if (window.facebookPixelId == null || !window.fbq) {
        return
    }
    const key = window.facebookPixelKey

    logger.info(`Record Facebook Pixel Event ${key}/${eventName} - ${JSON.stringify(eventData)}`)

    try {
        fbq('trackCustom', eventName, eventData)
        // window.fbq('track', eventName, eventData)
    } catch (e) {
        logger.error('Failed to track Facebook Pixel event')
    }
}

function hubspotTrackingKey(key, hubspotId) { return `hubspot_${key}:${hubspotId}` }
export function loadHubspotTracking(k, hubspotId) {
    for (let i = 0; i < 100; i++) {
        logger.info("HUBSPOT: Enable hubspot tracking")
    }
    const key = hubspotTrackingKey(k, hubspotId)
    // logger.info(`Try Init Facebook Pixel ${key} - prev : ${window.facebookPixelKey}`)
    const scriptKey = `${key}_script`
    if (window.hubspotTrackingKey == key) {
        // logger.info(`Facebook Pixel ${key} already set up`)
        return
    }

    // Strip out previous version
    if (window.hubspotTrackingKey != null) {
        const prevKey = `${window.hubspotTrackingKey}_script`
        const existing = document.getElementById(prevKey)
        if (existing) {
            logger.info(`Remove previous hubspot ${prevKey}`)
            existing.remove()
        } else {
            logger.error(`Failed to remove previous hubspot ${prevKey}`)
        }

        // Remove all scripts containing pixelId
        /*const scripts = [...document.getElementsByTagName('script')]
        scripts.forEach((x) => {
            if (x.src.indexOf(`signals/config/${window.facebookPixelId}`) > 0) {
                x.remove()
            }
        })*/

        window.hubspotTrackingKey = null
        window.hubspotTrackingId = null
    }

    const src = `https://js-na2.hs-scripts.com/${hubspotId}.js`

    logger.info(`Intialize Hubspot Tracking ${key}`)
    window.hubspotTrackingKey = key
    window.hubspotTrackingId = hubspotId

    return fnc.loadScript(scriptKey, src)
        .then((x) => {
            return x
        })
        .catch(logger.error)
}

export function hubspotIdentifyUser(email = null, analytics = {}) {
    var _hsq = window._hsq = window._hsq || [];
    let idData = {
        email: email,
        // id: fnc.getCookie('hubspotutk'),
        // utk: fnc.getCookie('hubspotutk'),
        // hubspotutk: fnc.getCookie('hubspotutk'),
        ...analytics
    }
    // Remove all null values
    idData = Object.fromEntries(Object.entries(idData).filter(([k, v]) => v != null))

    if (window._hsq) {
        _hsq.push(['identify', idData])
        _hsq.push(['trackPageView']);
    }
}

export function hubspotTrackPageView() {
    // 
    try {
        if (window._hsq) {
            window._hsq.push(['setPath', window.location.pathname]);
            window._hsq.push(['trackPageView']);
        }
    } catch (e) {
        logger.error("HUBSPOT: Failed to track page view")
    }
}

export function getFacebookTrackingId() {
    try {
        const queryString = window.location.search
        const parameters = new URLSearchParams(queryString)
        return parameters.get('fbclid')
    } catch (e) {
    }
    return null
}

export function getGoogleTrackingId() {
    try {
        const queryString = window.location.search
        const parameters = new URLSearchParams(queryString)
        return parameters.get('gclid')
    } catch (e) {
    }
    return null
}


export function loadReCaptcha() {
    if (!getReCaptchaSiteKey()) {
        logger.info("ReCaptcha not available")
        return new Promise((resolve, reject) => resolve())
    }
    return fnc.loadScript("recaptcha", `https://www.google.com/recaptcha/api.js?render=${getReCaptchaSiteKey()}`)
        .then((x) => {
            logger.info("ReCaptcha Loaded")
            return x
        })
        .catch((x) => {
            logger.info("ReCaptcha not available")
            return null
        })
    // <script src="https://www.google.com/recaptcha/api.js?render=%REACT_APP_GOOGLE_SITEKEY%"></script>
}

// As promise
export function executeReCaptcha() {
    return new Promise((resolve, reject) => {
        if (!getReCaptchaSiteKey() || window.grecaptcha == null) {
            reject("ReCaptcha not available")
            return
        }
        try {
            window.grecaptcha.ready(function () {
                window.grecaptcha.execute(getReCaptchaSiteKey(), { action: 'homepage' }).then(function (token) {
                    if (!token) {
                        reject("ReCaptcha failed")
                        return
                    }
                    resolve(token)
                }).catch(reject)

            })
        } catch (e) {
            reject(e)
        }
    })
}

import * as Sentry from "@sentry/react";
export function initializeSentry() {
    logger.info("Initialize Sentry", getSentryDSN())
    Sentry.init({
        dsn: getSentryDSN(),
        integrations: [
            Sentry.browserTracingIntegration(),
        ],
        // Tracing
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        //   tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    });
}
